import React, { FC, useState, useCallback } from 'react';
import { Box, CircularProgress, Alert, Typography, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import clientService from 'src/clientService'; // Assuming clientService is available for API calls

interface AudioPlayerProps {
  fileId: string;
}

const AudioPlayer: FC<AudioPlayerProps> = ({ fileId }) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPlayed, setHasPlayed] = useState<boolean>(false);

  const fetchAndPlayAudio = useCallback(async () => {
    if (hasPlayed) return; // Prevent re-fetching if already played or attempted

    setLoading(true);
    setError(null);
    try {
      // Assuming an API endpoint to get the download URL for a fileId
      const response = await clientService.api.getDownloadUrlApiV1AdminFilesFileIdDownloadUrlGet(fileId);
      setAudioUrl(response.data.download_url); // Adjust based on actual API response structure
      setHasPlayed(true);
    } catch (err) {
      console.error("Failed to get audio URL:", err);
      setError("Failed to load audio.");
    } finally {
      setLoading(false);
    }
  }, [fileId, hasPlayed]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={50}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!hasPlayed) {
    return (
      <IconButton onClick={fetchAndPlayAudio} color="primary" aria-label="play audio">
        <PlayArrowIcon />
      </IconButton>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 300 }}>
      {audioUrl ? (
        <audio controls autoPlay src={audioUrl} style={{ width: '100%' }}>
          Your browser does not support the audio element.
        </audio>
      ) : (
        <Typography>No audio to play.</Typography>
      )}
    </Box>
  );
};

export default AudioPlayer;
